/**
 * Employee Dashboard
 *
 * Personal dashboard for employees showing their evaluations, tasks, and growth.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { useEvaluations } from '../evaluations/useEvaluations';
import { useEmployeeAssignedTasks } from '../employees/useEmployeeTasks';
import { ScoreDisplay } from '../evaluations/wizard/ScoreInput';
import { ActionHolderAgreements } from '../agreements/ActionHolderAgreements';
import type { EvaluationStatus } from '../../lib/evaluationsApi';

const STATUS_CONFIG: Record<EvaluationStatus, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Concept', color: 'text-yellow-800', bgColor: 'bg-yellow-100' },
  submitted: { label: 'Ingediend', color: 'text-blue-800', bgColor: 'bg-blue-100' },
  in_review: { label: 'In Review', color: 'text-purple-800', bgColor: 'bg-purple-100' },
  final: { label: 'Definitief', color: 'text-green-800', bgColor: 'bg-green-100' },
  cancelled: { label: 'Geannuleerd', color: 'text-gray-600', bgColor: 'bg-gray-100' },
};

export function EmployeeDashboard() {
  const { user } = useAuth();
  const { data: evaluations, isLoading: isLoadingEvals } = useEvaluations();
  const employeeId = user?.employeeId;
  const { data: assignedTasks, isLoading: isLoadingTasks } = useEmployeeAssignedTasks(employeeId || 0);

  // Filter evaluations for this employee
  const myEvaluations = evaluations?.data?.filter((e) => e.employeeId === employeeId) || [];
  const currentEvaluation = myEvaluations.find((e) => e.status !== 'final' && e.status !== 'cancelled');
  const completedEvaluations = myEvaluations.filter((e) => e.status === 'final');
  const latestFinal = completedEvaluations[0];

  // Calculate average score from completed evaluations
  const avgScore = completedEvaluations.length > 0
    ? completedEvaluations.reduce((sum, e) => sum + parseFloat(e.totalScore || '0'), 0) / completedEvaluations.length
    : null;

  // Lijsten houden alles, tellers tellen actief. `tasks` voedt de takenlijst
  // verderop en blijft daarom compleet (met een markering bij het vervallene);
  // de twee getallen bovenaan rekenen over `activeTasks` -- dezelfde definitie
  // als `employees.totaal_punten` en het niveau dat daaruit volgt.
  // Spec: _bmad-output/implementation-artifacts/spec-weergave-actief-puntentotaal.md
  //
  // Het puntentotaal komt uit `totalWeging` in diezelfde payload en wordt hier
  // NIET nagerekend: de server-definitie is de gezaghebbende (`getEmployeeTasks`
  // sommeert daar over de actieve taken, net als `recalculateEmployeeNiveau` voor
  // `employees.totaal_punten`). Zelf optellen zou een tweede definitie
  // introduceren die stilzwijgend uiteen kan lopen -- precies de oorzaak die dit
  // onderwerp opruimt. Het aantal is er niet als veld, dus dat telt de UI wel
  // zelf, over dezelfde actieve deelverzameling.
  const tasks = assignedTasks?.tasks || [];
  const activeTasks = tasks.filter((t) => t.isActief !== false);
  const totalWeging = assignedTasks?.totalWeging ?? 0;

  return (
    <div data-testid="employee-dashboard" className="space-y-6">
      {/* Welcome Header — XXtract-palet navy (FR-23): geen gradient-banner meer (Epic 5, Story 5.1) */}
      <div className="bg-[#2F5A7A] rounded-lg p-6 text-white">
        <h2 className="text-xl font-semibold">Welkom terug, {user?.name}!</h2>
        <p className="mt-1 text-white/80">
          Bekijk uw taken, beoordelingen en groeipad.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div
          data-testid="dashboard-stat-toegewezen-taken"
          className="bg-white p-4 rounded-lg border border-gray-200 text-center"
        >
          <p className="text-2xl font-bold text-gray-900">{activeTasks.length}</p>
          <p className="text-sm text-gray-500">Toegewezen Taken</p>
        </div>
        <div
          data-testid="dashboard-stat-totale-weging"
          className="bg-white p-4 rounded-lg border border-gray-200 text-center"
        >
          <p className="text-2xl font-bold text-gray-900">{totalWeging}%</p>
          <p className="text-sm text-gray-500">Totale Weging</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
          <p className="text-2xl font-bold text-gray-900">{completedEvaluations.length}</p>
          <p className="text-sm text-gray-500">Beoordelingen</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
          <p className="text-2xl font-bold text-green-700">
            {avgScore ? avgScore.toFixed(2) : '—'}
          </p>
          <p className="text-sm text-green-600">Gem. Score</p>
        </div>
      </div>

      {/* Action Holder Agreements - Show if employee has any */}
      {employeeId && <ActionHolderAgreements employeeId={employeeId} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Evaluation */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Huidige Beoordeling</h3>
          </div>
          <div className="p-6">
            {isLoadingEvals ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : currentEvaluation ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Periode</p>
                    <p className="font-medium text-gray-900">
                      {new Date(currentEvaluation.periodeStart).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })} -{' '}
                      {new Date(currentEvaluation.periodeEnd).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${STATUS_CONFIG[currentEvaluation.status].bgColor} ${STATUS_CONFIG[currentEvaluation.status].color}`}>
                    {STATUS_CONFIG[currentEvaluation.status].label}
                  </span>
                </div>

                {currentEvaluation.status === 'draft' && !currentEvaluation.selfEvaluationAt && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Actie vereist:</strong> Vul uw zelf-evaluatie in voordat de manager uw beoordeling kan starten.
                    </p>
                    <Link
                      to={`/mijn-beoordeling/${currentEvaluation.id}`}
                      className="mt-2 inline-flex items-center text-sm font-medium text-yellow-700 hover:text-yellow-900"
                    >
                      Zelf-evaluatie invullen →
                    </Link>
                  </div>
                )}

                {currentEvaluation.status === 'submitted' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      Uw beoordeling is ingediend en wacht op review door HR.
                    </p>
                  </div>
                )}

                {currentEvaluation.status === 'in_review' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800">
                      Uw beoordeling wordt momenteel beoordeeld door HR.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">Geen actieve beoordeling</p>
                <p className="text-xs text-gray-400">Uw manager zal een nieuwe beoordeling starten wanneer nodig.</p>
              </div>
            )}
          </div>
        </div>

        {/* Tasks Overview */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Mijn Taken</h3>
            <Link
              to="/mijn-taken"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Bekijk alle →
            </Link>
          </div>
          <div className="p-6">
            {isLoadingTasks ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    data-testid={`dashboard-task-row-${task.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {task.name}
                        {task.isActief === false && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
                            Gearchiveerd &ndash; telt niet mee
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">Weging: {task.weging}%</p>
                    </div>
                  </div>
                ))}
                {tasks.length > 5 && (
                  <p className="text-xs text-gray-500 text-center pt-2">
                    + {tasks.length - 5} meer taken
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Nog geen taken toegewezen</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Evaluation History */}
      {completedEvaluations.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Beoordelingsgeschiedenis</h3>
            <Link
              to="/mijn-historie"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Bekijk alle →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Periode</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Takenscore</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Competenties</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Totaal</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Afgerond</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Print</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {completedEvaluations.slice(0, 3).map((evaluation) => (
                  <tr key={evaluation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })} -{' '}
                      {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {evaluation.taskScore ? (
                        <ScoreDisplay score={Math.round(parseFloat(evaluation.taskScore))} size="sm" showLabel={false} />
                      ) : '—'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {evaluation.competencyScore ? (
                        <ScoreDisplay score={Math.round(parseFloat(evaluation.competencyScore))} size="sm" showLabel={false} />
                      ) : '—'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {evaluation.totalScore ? (
                        <span className="text-lg font-bold text-green-700">{parseFloat(evaluation.totalScore).toFixed(2)}</span>
                      ) : '—'}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-500">
                      {evaluation.finalizedAt
                        ? new Date(evaluation.finalizedAt).toLocaleDateString('nl-NL')
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => window.open(`/beoordeling/${evaluation.id}/print`, '_blank')}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Scoringsformulier afdrukken"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Latest Score with Growth Indicator */}
      {latestFinal && completedEvaluations.length >= 2 && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Groei Overzicht</h3>
          <div className="flex items-center gap-8">
            <div>
              <p className="text-sm text-gray-500">Laatste Score</p>
              <p className="text-3xl font-bold text-green-700">
                {parseFloat(latestFinal.totalScore || '0').toFixed(2)}
              </p>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500 mb-2">Score Trend</p>
              <div className="flex items-center gap-2">
                {completedEvaluations.slice(0, 5).reverse().map((e, i) => {
                  const score = parseFloat(e.totalScore || '0');
                  const height = Math.max(20, (score / 5) * 60);
                  return (
                    <div key={e.id} className="flex flex-col items-center">
                      <div
                        className={`w-8 rounded-t ${i === completedEvaluations.length - 1 ? 'bg-green-500' : 'bg-blue-300'}`}
                        style={{ height: `${height}px` }}
                      ></div>
                      <span className="text-xs text-gray-400 mt-1">
                        {new Date(e.periodeEnd).toLocaleDateString('nl-NL', { month: 'short' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
